// @givmo/auth — App Check (ReCaptcha Enterprise, auto-refresh, FAIL-CLOSED) for all apps.
//
// Ported from the Flutter `_initAppCheck()` (givmo-webapp + givmo-donate-web `main.dart`):
//   appCheck.activate(webProvider: ReCaptchaEnterpriseProvider(siteKey))
// plus the `AppCheckInterceptor` (same-origin gating + getToken → `X-Firebase-AppCheck`). The web
// SDK equivalent is `initializeAppCheck({ provider: new ReCaptchaEnterpriseProvider(siteKey),
// isTokenAutoRefreshEnabled: true })` + `getToken`.
//
// FAIL-CLOSED (the spec's hard requirement): `getGivmoAppCheckToken` does NOT swallow a token
// failure into a blank header the way the iOS app-check logging tolerated; if App Check can't mint a
// token, the attested request MUST NOT proceed unattested. The token provider throws, and the
// api-client middleware that consumes it is expected to abort the request rather than send it
// without `X-Firebase-AppCheck`. (Flutter's interceptor logged-and-continued; staging/prod backend
// enforcement then rejected the unattested call — same net effect, but we make the client-side
// contract explicit and fail BEFORE the wire so the failure is attributable to App Check, not a
// downstream 401/403.) The optional reCAPTCHA token-auto-refresh keeps a fresh token warm so steady
// state never blocks on a mint.

import {
  initializeAppCheck,
  getLimitedUseToken,
  getToken,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import type { FirebaseApp } from 'firebase/app';

/** Config a consuming app injects to stand App Check up. The site key is per-environment. */
export interface AppCheckConfig {
  /** ReCaptcha Enterprise site key for this environment (from the consuming app's env). */
  reCaptchaEnterpriseSiteKey: string;
  /**
   * Keep a fresh App Check token warm in the background. Defaults to `true` (matches the Flutter
   * activation). Disable only in tests or environments that mint per-call.
   */
  isTokenAutoRefreshEnabled?: boolean;
  /**
   * Debug token for local/CI. When set, assigns `self.FIREBASE_APPCHECK_DEBUG_TOKEN` BEFORE
   * `initializeAppCheck` (the SDK reads it at init). `true` asks the SDK to print a fresh debug
   * token to the console to register once. NEVER set in deployed environments.
   */
  debugToken?: string | boolean;
}

/**
 * Initialize App Check with ReCaptcha Enterprise + auto-refresh. Idempotent per app: a second call
 * for the same `FirebaseApp` returns the already-initialized instance (the SDK throws
 * `appCheck/already-initialized` on re-init, which we catch and translate to a reuse).
 */
export function initGivmoAppCheck(app: FirebaseApp, config: AppCheckConfig): AppCheck {
  if (config.debugToken !== undefined) {
    // The SDK reads `self.FIREBASE_APPCHECK_DEBUG_TOKEN` at init time to use a debug provider instead
    // of real attestation. We write it on `globalThis` (=== `self` in a browser/worker, and defined
    // everywhere incl. SSR/Node), so the property the SDK reads is set without depending on a
    // browser-only `self` global existing.
    (
      globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = config.debugToken;
  }
  return initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(config.reCaptchaEnterpriseSiteKey),
    isTokenAutoRefreshEnabled: config.isTokenAutoRefreshEnabled ?? true,
  });
}

/**
 * Mint an App Check token for the `X-Firebase-AppCheck` request header — FAIL-CLOSED.
 *
 * Throws if a token cannot be obtained, so the api-client middleware aborts the attested request
 * rather than sending it unattested (see file header). Pass `forceRefresh` to bypass the cached
 * token (e.g. after a 401 attestation rejection).
 *
 * Returns just `{ token }` — the SDK's web `AppCheckTokenResult` exposes only the JWT string (no
 * `expireTimeMillis`; that field exists on the React Native / compat SDK, not the JS modular one).
 * The SDK already caches + auto-refreshes the token internally, so the api-client mints on demand
 * here rather than caching against an expiry it cannot read.
 */
export async function getGivmoAppCheckToken(
  appCheck: AppCheck,
  forceRefresh = false,
): Promise<{ token: string }> {
  // `getToken` itself rejects on a hard failure; a defensive guard also rejects on an empty token so
  // an empty string can never slip through into a header (fail-closed).
  const result = await getToken(appCheck, forceRefresh);
  if (!result.token) {
    throw new Error(
      'App Check returned an empty token (fail-closed: refusing to send unattested).',
    );
  }
  return { token: result.token };
}

/**
 * Mint a fresh, limited-use App Check token for a replay-protected backend request — FAIL-CLOSED.
 *
 * Firebase's `getLimitedUseToken(appCheck)` bypasses the standard token cache and mints a token
 * intended for one request. It has no `forceRefresh` argument. As with {@link getGivmoAppCheckToken},
 * a mint failure or empty result rejects so a replay-protected request never goes out unattested.
 */
export async function getGivmoLimitedUseAppCheckToken(
  appCheck: AppCheck,
): Promise<{ token: string }> {
  const result = await getLimitedUseToken(appCheck);
  if (!result.token) {
    throw new Error(
      'App Check returned an empty limited-use token (fail-closed: refusing to send unattested).',
    );
  }
  return { token: result.token };
}

// ── Phone-auth reCAPTCHA × App Check ReCaptcha Enterprise coexistence ────────────────────────────
//
// The spec calls out designing the coexistence of Firebase PHONE-AUTH reCAPTCHA with App Check
// ReCaptcha Enterprise on the same consumer pages. They are two SEPARATE reCAPTCHA integrations that
// must not be conflated:
//
//   1. App Check uses a ReCaptcha ENTERPRISE site key (`ReCaptchaEnterpriseProvider`), initialized
//      ONCE per app at boot, invisible, and auto-refreshing. It attests every API call.
//   2. Firebase phone auth uses its OWN reCAPTCHA via `RecaptchaVerifier`, created PER phone-sign-in
//      attempt and bound to a specific DOM container/button. It is NOT the App Check provider and
//      does NOT use the App Check site key — Firebase manages the phone reCAPTCHA site key server-
//      side; the client only supplies the container + size.
//
// Because they are independent, they coexist without interference PROVIDED:
//   • App Check is initialized exactly once (we guarantee that here / via the SDK's single-init).
//   • Each `RecaptchaVerifier` is created against a DISTINCT container element and `clear()`-ed after
//     use, so a second phone attempt doesn't collide with a stale widget. (`consumer.ts` owns that
//     lifecycle.)
//   • Invisible phone reCAPTCHA is preferred so the user never sees two challenges; the App Check
//     Enterprise challenge is already invisible.
//
// `PHONE_AUTH_RECAPTCHA_NOTES` exists so this design constraint is discoverable from code (and so a
// future reviewer doesn't "consolidate" the two reCAPTCHAs onto one key — they CANNOT share a key).
export const PHONE_AUTH_RECAPTCHA_NOTES = {
  appCheckProvider: 'ReCaptchaEnterpriseProvider (App Check site key, init once, auto-refresh)',
  phoneAuthVerifier:
    'RecaptchaVerifier (Firebase-managed phone reCAPTCHA, per-attempt, distinct container)',
  shareSiteKey: false,
  preferInvisiblePhoneRecaptcha: true,
} as const;
